/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AuthApi, type LoginRequest, type RegisterRequest, type AuthResponse, type UserInfo, type UpdateProfileRequest } from '@/api/backend'
import { BackendConfiguration } from '@/api/backend/base'
import { authService } from '@/services/AuthService'
import { logger } from '@/services/logger'
import { useAppStore } from '@/stores/app'
import type { AxiosError } from 'axios'
import '@/types/chrome' // Import chrome types for global Window interface extension



interface User {
  id: string
  email: string
  name: string
  subscriptionType: 'free' | 'premium'
  emailVerified?: boolean
  language?: string
  theme?: 'light' | 'dark' | 'system'
}

interface AuthError {
  message: string
  validationErrors?: { [key: string]: string[] }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const accessToken = ref<string | null>(null)
  const tokenExpiresAt = ref<Date | null>(null)

  // API instance
  const authApi = new AuthApi(BackendConfiguration)

  // Token refresh timer
  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  // Getters
  const isTokenExpired = computed(() => {
    if (!tokenExpiresAt.value) return true
    return new Date() >= tokenExpiresAt.value
  })

  const shouldRefreshToken = computed(() => {
    if (!tokenExpiresAt.value || !accessToken.value) return false
    // Refresh token if it expires within 5 minutes
    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000)
    return tokenExpiresAt.value <= fiveMinutesFromNow
  })

  // Helper functions
  function mapUserInfoToUser(userInfo: UserInfo): User {
    return {
      id: userInfo.id || '',
      email: userInfo.email || '',
      name: userInfo.name || '',
      subscriptionType: userInfo.isPremium ? 'premium' : 'free',
      emailVerified: userInfo.emailVerified,
      language: userInfo.language || 'en',
      theme: userInfo.theme as 'light' | 'dark' | 'system' || 'system'
      // Note: usageCount and maxFreeUsage are now handled by subscription store
    }
  }

  function setAuthData(authResponse: AuthResponse) {
    if (authResponse.token && authResponse.user) {
      accessToken.value = authResponse.token
      tokenExpiresAt.value = authResponse.expiresAt ? new Date(authResponse.expiresAt) : null
      user.value = mapUserInfoToUser(authResponse.user)
      isAuthenticated.value = true

      // Sync user's language with app store
      const appStore = useAppStore()
      if (user.value.language && user.value.language !== appStore.currentLanguage) {
        appStore.setLanguage(user.value.language)
      }

      // Store auth state in localStorage
      const authData = {
        token: authResponse.token,
        expiresAt: authResponse.expiresAt,
        user: user.value
      }
      localStorage.setItem('noorwriter-auth', JSON.stringify(authData))

      // Schedule automatic token refresh
      scheduleTokenRefresh()

      // Sync authentication to extension if available
      syncAuthToExtension()
    }
  }

  function clearAuthData() {
    accessToken.value = null
    tokenExpiresAt.value = null
    user.value = null
    isAuthenticated.value = false
    localStorage.removeItem('noorwriter-auth')

    // Clear refresh timer
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  /**
   * Clear any error-related state (useful when switching between auth forms)
   */
  function clearErrorState() {
    // Reset loading state if it's stuck
    isLoading.value = false
  }

  function scheduleTokenRefresh() {
    // Clear existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer)
    }

    if (!tokenExpiresAt.value) return

    // Calculate time until token should be refreshed (5 minutes before expiry)
    const refreshTime = new Date(tokenExpiresAt.value.getTime() - 5 * 60 * 1000)
    const timeUntilRefresh = refreshTime.getTime() - Date.now()

    if (timeUntilRefresh > 0) {
      refreshTimer = setTimeout(async () => {
        if (isAuthenticated.value && !isTokenExpired.value) {
          await refreshToken()
        }
      }, timeUntilRefresh)
    }
  }

  // Actions
  async function signIn(email: string, password: string, rememberMe: boolean = false) {
    isLoading.value = true
    try {
      const loginRequest: LoginRequest = {
        email,
        password,
        rememberMe,
        deviceInfo: navigator.userAgent
      }

      const response = await authApi.login(loginRequest)
      const authResponse = response.data

      if (authResponse.success && authResponse.token) {
        setAuthData(authResponse)
        return { success: true }
      } else {
        const error: AuthError = {
          message: authResponse.error || 'Login failed',
          validationErrors: authResponse.validationErrors || undefined
        }
        return { success: false, error }
      }
    } catch (error: unknown) {
      logger.error('Login failed', error instanceof Error ? error : undefined, { email }, 'Auth')
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (error as { message?: string })?.message
        || 'Login failed'
      return { success: false, error: { message: errorMessage } }
    } finally {
      isLoading.value = false
    }
  }

  async function signUp(email: string, password: string, name: string, confirmPassword?: string, language: string = 'en') {
    isLoading.value = true
    try {
      // Import timezone detection utility
      const { detectUserTimezone, detectUserCulture } = await import('@/utils/timezoneDetection')

      const registerRequest: RegisterRequest = {
        name,
        email,
        password,
        confirmPassword: confirmPassword || password,
        language,
        culture: detectUserCulture(),
        timezone: detectUserTimezone(),
        sendEmailVerification: true
      }

      const response = await authApi.register(registerRequest)
      const authResponse = response.data

      if (authResponse.success && authResponse.token) {
        setAuthData(authResponse)
        return { success: true }
      } else {
        const error: AuthError = {
          message: authResponse.error || 'Registration failed',
          validationErrors: authResponse.validationErrors || undefined
        }
        return { success: false, error }
      }
    } catch (error: unknown) {
      logger.error('Registration failed', error instanceof Error ? error : undefined, { email }, 'Auth')
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (error as { message?: string })?.message
        || 'Registration failed'
      return { success: false, error: { message: errorMessage } }
    } finally {
      isLoading.value = false
    }
  }

  async function signOut() {
    try {
      // Call backend logout endpoint if authenticated
      if (isAuthenticated.value) {
        await authApi.logout()
      }
    } catch (error) {
      logger.warn('Logout error', { error: error instanceof Error ? error.message : String(error) }, 'Auth')
    } finally {
      clearAuthData()

      // Clear subscription data as well
      const { useSubscriptionStore } = await import('@/stores/subscription')
      const subscriptionStore = useSubscriptionStore()
      subscriptionStore.clearSubscriptionData()
    }
  }

  async function refreshToken() {
    try {
      if (!accessToken.value) return false

      const response = await authApi.refreshToken()
      const authResponse = response.data

      if (authResponse.success && authResponse.token) {
        setAuthData(authResponse)
        return true
      } else {
        clearAuthData()
        return false
      }
    } catch (error) {
      logger.error('Token refresh error:', error instanceof Error ? error : undefined, {}, 'auth')
      clearAuthData()
      return false
    }
  }

  async function getCurrentUser() {
    try {
      if (!isAuthenticated.value || isTokenExpired.value) {
        return null
      }

      const response = await authApi.getCurrentUser()
      const userInfo = response.data

      if (userInfo) {
        user.value = mapUserInfoToUser(userInfo)

        // Sync user's language with app store
        const appStore = useAppStore()
        if (user.value.language && user.value.language !== appStore.currentLanguage) {
          appStore.setLanguage(user.value.language)
        }

        return user.value
      }
      return null
    } catch (error) {
      logger.error('Get current user error:', error instanceof Error ? error : undefined, {}, 'auth')

      // If we get a 401 error, the token is invalid - clear authentication data
      if ((error as AxiosError)?.response?.status === 401) {
        logger.warn('Token is invalid (401), clearing authentication data', {}, 'auth')
        clearAuthData()
      }

      return null
    }
  }

  async function updateProfile(profileData: { name: string; language?: string; theme?: 'light' | 'dark' | 'system' }) {
    try {
      if (!isAuthenticated.value) {
        throw new Error('User not authenticated')
      }

      const updateRequest: UpdateProfileRequest = {
        name: profileData.name,
        language: profileData.language || user.value?.language || 'en',
        theme: profileData.theme
      }

      const response = await authApi.updateProfile(updateRequest)
      const updatedUserInfo = response.data

      if (updatedUserInfo) {
        user.value = mapUserInfoToUser(updatedUserInfo)

        // Store theme preference for immediate access
        if (user.value.theme) {
          localStorage.setItem('noorwriter-user-theme', user.value.theme)
        }

        // Update localStorage
        const savedAuth = localStorage.getItem('noorwriter-auth')
        if (savedAuth) {
          const authData = JSON.parse(savedAuth)
          authData.user = user.value
          localStorage.setItem('noorwriter-auth', JSON.stringify(authData))
        }

        return { success: true, user: user.value }
      }

      return { success: false, error: { message: 'Failed to update profile' } }
    } catch (error: unknown) {
      logger.error('Update profile error:', error instanceof Error ? error : undefined, {}, 'auth')
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (error as { message?: string })?.message
        || 'Failed to update profile'
      return { success: false, error: { message: errorMessage } }
    }
  }

  function initializeAuth() {
    const savedAuth = localStorage.getItem('noorwriter-auth')
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth)

        // Validate required fields
        if (!authData.token || !authData.user) {
          logger.warn('Invalid auth data in localStorage, clearing', {}, 'auth')
          localStorage.removeItem('noorwriter-auth')
          return
        }

        // Check if token is expired
        if (authData.expiresAt && new Date(authData.expiresAt) <= new Date()) {
          logger.info('Token expired, clearing auth data', {}, 'auth')
          localStorage.removeItem('noorwriter-auth')
          return
        }

        accessToken.value = authData.token
        tokenExpiresAt.value = authData.expiresAt ? new Date(authData.expiresAt) : null
        user.value = authData.user
        isAuthenticated.value = true

        // Sync user's language with app store
        const appStore = useAppStore()
        if (user.value?.language && user.value.language !== appStore.currentLanguage) {
          appStore.setLanguage(user.value.language)
        }

        // Load theme preference from localStorage if available
        const savedTheme = localStorage.getItem('noorwriter-user-theme') as 'light' | 'dark' | 'system'
        if (savedTheme && user.value) {
          user.value.theme = savedTheme
        }

        // Schedule automatic token refresh
        scheduleTokenRefresh()

        // Try to refresh user data from backend only if we have valid authentication
        // Note: This may still fail with 401 if the token is invalid on the server side,
        // but the error handling in getCurrentUser() will clear the auth data in that case
        if (isAuthenticated.value && !isTokenExpired.value) {
          getCurrentUser()
        }
      } catch (error) {
        logger.error('Auth initialization error:', error instanceof Error ? error : undefined, {}, 'auth')
        localStorage.removeItem('noorwriter-auth')
      }
    }
  }

  /**
   * Authenticate using a token from the extension
   */
  async function authenticateWithToken(token: string) {
    try {
      isLoading.value = true

      // Check if this is a redirect token (shorter, one-time use)
      if (token.length < 100) {
        // This is likely a redirect token, exchange it for a full JWT
        return await exchangeRedirectToken(token)
      } else {
        // This is a full JWT token, validate it directly
        return await validateJwtToken(token)
      }
    } catch (error) {
      logger.error('Token authentication error:', error instanceof Error ? error : undefined, {}, 'auth')
      clearAuthData()
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Exchange a redirect token for a full JWT token
   */
  async function exchangeRedirectToken(redirectToken: string) {
    try {
      // Use AuthService which handles unauthenticated API calls to avoid sending Bearer token headers
      // This endpoint doesn't require authentication and may fail with 401 if an expired token is sent
      const response = await authService.exchangeRedirectToken(redirectToken)
      const authResponse = response.data
      logger.info('Token exchange response:', { success: authResponse?.success, hasToken: !!authResponse?.token, hasUser: !!authResponse?.user }, 'auth')

      if (authResponse && authResponse.success && authResponse.token && authResponse.user) {
        setAuthData(authResponse)
        return true
      } else {
        logger.error('Invalid token exchange response:', undefined, { authResponse }, 'auth')
        clearAuthData()
        return false
      }
    } catch (error) {
      logger.error('Redirect token exchange error:', error instanceof Error ? error : undefined, {}, 'auth')
      clearAuthData()
      return false
    }
  }

  /**
   * Validate a full JWT token
   */
  async function validateJwtToken(token: string) {
    try {
      // Set the token temporarily to make authenticated requests
      accessToken.value = token

      // Get current user data to validate the token
      const response = await authApi.getCurrentUser()
      const userInfo = response.data

      if (userInfo && userInfo.id) {
        // Token is valid, set up authentication state
        const authResponse: AuthResponse = {
          success: true,
          token: token,
          expiresAt: undefined, // We don't have expiry info from extension token
          user: userInfo
        }

        setAuthData(authResponse)
        return true
      } else {
        // Token is invalid
        clearAuthData()
        return false
      }
    } catch (error) {
      logger.error('JWT token validation error:', error instanceof Error ? error : undefined, {}, 'auth')
      clearAuthData()
      return false
    }
  }

  /**
   * Sync authentication state to the extension if available
   */
  function syncAuthToExtension() {
    try {
      // Use the global chrome object that's already typed
      const chrome = window.chrome

      if (typeof window !== 'undefined' && chrome && chrome.runtime) {
        // Try to send authentication data to the extension
        // Include user's theme preference from localStorage
        const userTheme = localStorage.getItem('noorwriter-user-theme') || user.value?.theme

        const authData = {
          token: accessToken.value,
          expiresAt: tokenExpiresAt.value?.toISOString(),
          user: user.value,
          theme: userTheme
        }

        // Send message to extension background script
        chrome.runtime.sendMessage(
          'oieeijmjdfimebgclcijdicdpkhcgboo', // Extension ID - this should be configurable
          {
            action: 'syncAuth',
            data: authData
          },
          (_response: unknown) => {
            if (chrome.runtime?.lastError) {
              // Extension not available or not responding - this is normal
              logger.debug('Extension not available for auth sync', {}, 'auth')
            } else {
              logger.debug('Auth synced to extension', { theme: userTheme }, 'auth')
            }
          }
        )
      }
    } catch (error) {
      // Silently fail - extension sync is optional
      logger.debug('Extension auth sync failed:', { error: error instanceof Error ? error.message : String(error) }, 'auth')
    }
  }

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    accessToken,
    tokenExpiresAt,

    // Getters
    isTokenExpired,
    shouldRefreshToken,

    // Actions
    signIn,
    signUp,
    signOut,
    refreshToken,
    getCurrentUser,
    updateProfile,
    initializeAuth,
    scheduleTokenRefresh,
    authenticateWithToken,
    clearErrorState,
  }
})
